import CombineAudienceName from '../../../components/CombineAudienceName';
import CombineAudienceStepper from '../../../components/CombineAudienceStepper';
import CombinedAudienceBuilder from '../../../components/CombinedAudienceBuilder/index.jsx';
import GenerateAudience from '../../../components/GenerateAudience/index.jsx';
import GhostLoader from 'react-ghost-loader';
import InlineAudienceExport from '../../../components/CombinedAudienceExport/InlineAudienceExport';
import React, { useEffect, useMemo, useState } from 'react';
import TotalAudienceSize from '../totalAudienceSize';
import camelizeKeys from 'utils/camelizeKeys';
import {
  ATTAIN,
  ENUM_SYSTEM_USED,
  GENERATION_STATUS,
  Routes,
} from '../../../constants/index';
import { ArrowLeft } from '@phosphor-icons/react';
import { Grid, IconButton, theme } from '@klover/attain-design-system';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  addAudience,
  clearCurrentAudience,
  fetchCombinedAudience,
  setUIState,
} from '../slice';
import { getModeledAudeinceCounts, getModeledAudeinces } from '../../../helper/helper';
import { nanoid } from 'nanoid';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useHistory, useParams } from 'react-router-dom';
import * as Styled from './index.styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: '#fafafa',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px',
      boxShadow: '0px 3px 15px rgba(0, 0, 0, 0.07)',
      zIndex: 1,
      height: '81px'
    },
    totalAudienceSize: {
      display: 'flex',
    },
  })
);

export const CombineAudience = () => {
  const history = useHistory();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  const [operatorType, setOperatorType] = useState('OR');
  const [modeledAudiences, setModeledAudiences] = useState([]);
  const [modeledAudiencesCount, setModeledAudiencesCount] = useState([]);
  const [name, setName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isModeledAudienceLoading, setIsModeledAudienceLoading] =
    useState(false);
  const { audienceid } = useParams();
  const dispatch = useAppDispatch();

  const currentAudience = useAppSelector((state) => {
    return state.audienceReducer.currentAudience;
  });

  const currentGroup = useAppSelector((state) => {
    return state.audienceReducer.currentGroup;
  });

  const audienceLoading = useAppSelector(
    (state) => state.audienceReducer.ui.audienceLoading
  );

  const isAudienceLoading =
    audienceLoading.state === 'loading' || isModeledAudienceLoading;

  useEffect(() => {
    async function fetchMyAPI() {
      setIsModeledAudienceLoading(true);
      const response = await getModeledAudeinces();
      const allAudiences: any = [];
      response?.data.map((item: any) => {
        allAudiences.push({
          ...item,
          lalAudienceCount: camelizeKeys(item.lalAudienceCount),
        });
      });
      setModeledAudiences(allAudiences);
      setIsModeledAudienceLoading(false);
    }
    fetchMyAPI();
  }, []);

  function hasMissingIdType(referenceList: any, targetList: any) {
    return referenceList.some((refItem: { id: any; type: any; }) => {
        return !targetList.some((targetItem: { id: any; type: any; }) =>
            targetItem.id === refItem.id && targetItem.type === refItem.type
        );
    });
}
  
  useEffect(() => {
    async function fetchMyAPI() {
      const payload = (selectedAudiences as any[]).map(item => {
        return {
          ...item,
          lal_model_id: (modeledAudiences as any[])?.find((a: any) => `${a?.id}` === `${item?.id}`)?.lalModelId,
        };
      });
      setIsModeledAudienceLoading(true);
      const response = await getModeledAudeinceCounts({ selectedAudiences: payload });
      const allAudiences: any = [];
      response?.data.map((item: any) => {
        allAudiences.push({
          ...item,
          lalAudienceCount: camelizeKeys(item.lalAudienceCount),
        });
      });
      setModeledAudiencesCount(allAudiences);
      setIsModeledAudienceLoading(false);
    }
    if (activeStep === 1 && hasMissingIdType(selectedAudiences, modeledAudiencesCount)) {
      fetchMyAPI();  
    }    
  }, [activeStep]);

  useEffect(() => {
    if (currentAudience?.audiences) {
      const selectedAudiences = currentAudience?.audiences.map((item: any) => {
        return { ...item, lalModelPrecision: item?.lalModelPrecision || 1 };
      });
      setSelectedAudiences(selectedAudiences);
    }
    if (currentAudience?.operator) {
      setOperatorType(currentAudience?.operator);
    }
    if (
      currentAudience?.generationStatus &&
      currentAudience?.generationStatus === GENERATION_STATUS.IN_PROGRESS
    ) {
      setIsGenerating(true);
    } else {
      setIsGenerating(false);
    }
  }, [currentAudience]);

  const isGenerationCompleted = useMemo(() => {
    if (
      currentAudience?.generationStatus &&
      currentAudience?.generationStatus === GENERATION_STATUS.COMPLETED
    ) {
      return true;
    }
    return false;
  }, [currentAudience]);

  useEffect(() => {
    dispatch(setUIState({ value: false, uiPropertyName: 'errorFetching' }));
    if (audienceid) {
      dispatch(
        fetchCombinedAudience({
          id: audienceid,
        })
      );
    } else if (!audienceid && currentGroup) {
      const newAudience = {
        id: nanoid(),
        name: '',
        totalSize: 0,
        dimensions: [],
        dimensionGroups: [],
        createdAt: 0,
        updatedAt: 0,
      };
      dispatch(addAudience({ ...newAudience, lalModelType: 'attain' }));
      setSelectedAudiences([]);
      setOperatorType('OR');
    }
    return () => {
      dispatch(
        setUIState({
          value: false,
          uiPropertyName: 'errorFetching',
        })
      );
    };
  }, []);

  const handleClearAudience = (e: any) => {
    dispatch(clearCurrentAudience());
    history.push(
      `${Routes.AUDIENCE_STUDIO}?groupId=${currentGroup?.id || 'all'}`
    );
  };

  const classes = useStyles();
  return (
    <div style={{ background: '#F0EFF1', height: '90vh' }}>
      <Grid item container className={classes.root}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <IconButton
            color="default"
            disabled={isAudienceLoading}
            onClick={handleClearAudience}
          >
            <ArrowLeft size={theme.icons.lg} />
          </IconButton>
          <div style={{ marginLeft: 8 }} />
          <CombineAudienceName
            isAudienceLoading={false}
            isAudienceCreated={audienceid ? true : false}
            name={name}
            setName={setName}
            selectedAudiences={selectedAudiences}
            operatorType={operatorType}
          />
        </div>
        <Grid className={classes.totalAudienceSize}>
          <TotalAudienceSize
            lalModelType="attain"
            lalModelStatus={currentAudience?.generationStatus}
            isModeling={isGenerating}
            audienceType={ENUM_SYSTEM_USED.COMBINED_AUDIENCE}
          />
        </Grid>
      </Grid>
      {isAudienceLoading && <LoadingAudience />}
      {activeStep === 0 && !isAudienceLoading && (
        <CombinedAudienceBuilder
          selectedAudiences={selectedAudiences}
          setSelectedAudiences={setSelectedAudiences}
          operatorType={operatorType}
          setOperatorType={setOperatorType}
          modeledAudiences={modeledAudiences}
          isAudienceLoading={isAudienceLoading}
        />
      )}

      {activeStep === 1 && !isAudienceLoading && (
        <GenerateAudience
          selectedAudiences={selectedAudiences}
          setSelectedAudiences={setSelectedAudiences}
          operatorType={operatorType}
          modeledAudiences={modeledAudiences}
          modeledAudiencesCount={modeledAudiencesCount}
          isGenerating={isGenerating}
          setIsGenerating={setIsGenerating}
        />
      )}

      {activeStep === 2 && !isAudienceLoading && (
        <InlineAudienceExport
          lalModelType={ATTAIN}
          audienceName={currentAudience?.name}
          lastCompletedExportSize={currentAudience?.lastCompletedExportSize}
          audienceType={ENUM_SYSTEM_USED.COMBINED_AUDIENCE}
          isSelectAudienceVisible={true}
          modeledAudiences={modeledAudiences}
        />
      )}
      <CombineAudienceStepper
        activeStep={activeStep}
        setActiveStep={(step) => {
          setActiveStep(step);
        }}
        audienceName={currentAudience?.name}
        isAudienceLoading={isAudienceLoading}
        isGenerationCompleted={isGenerationCompleted}
      />
    </div>
  );
};

const LoadingAudience = () => {
  return (
    <Styled.Wrapper>
      <Styled.CardBody>
        <GhostLoader
          height="42px"
          heightRandom={0}
          width="42px"
          widthRandom={0}
        />

        <GhostLoader
          height="42px"
          heightRandom={0}
          width="100%"
          widthRandom={0}
        />
      </Styled.CardBody>
      <Styled.CardBody>
        <GhostLoader
          height="70px"
          heightRandom={0}
          width="10%"
          widthRandom={0}
        />
        <GhostLoader
          height="70px"
          heightRandom={0}
          width="80%"
          widthRandom={0}
        />
        <GhostLoader
          height="70px"
          heightRandom={0}
          width="10%"
          widthRandom={0}
        />
      </Styled.CardBody>
      <Styled.CardBody>
        <GhostLoader
          height="42px"
          heightRandom={0}
          width="10%"
          widthRandom={0}
        />
        <GhostLoader
          height="42px"
          heightRandom={0}
          width="10%"
          widthRandom={0}
        />
        <GhostLoader
          height="42px"
          heightRandom={0}
          width="70%"
          widthRandom={0}
        />
        <GhostLoader
          height="42px"
          heightRandom={0}
          width="10%"
          widthRandom={0}
        />
      </Styled.CardBody>
    </Styled.Wrapper>
  );
};
